import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { translate } from '@jsverse/transloco';

import { AppSharedModule } from 'app/app-shared.module';
import { ModalCloseType } from 'app/enums/modal-close-type';
import { WidgetDataSource } from 'app/models';
import { Widget } from 'app/providers/bridge-api/dashboard.service';
import {
  AccountMetadata,
  StorageAccountService,
} from 'app/providers/storage-api/blender-account.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { NumericMetadataFieldSelectorComponent } from '../numeric-metadata-field-selector/numeric-metadata-field-selector.component';
import { NumericMetadataField } from '../numeric-metadata-field-selector/numeric-metadata-field-selector.models';
import { MetadataField } from '../widget-add/widget-add.component';

export interface JourneyAnalyticItemSelectorDialogOptions {
  widget: Widget;
  widgetDataSource: WidgetDataSource;
  isNew: boolean;
  metadataFields: string[];
  sentimentScale: number;
}

export interface JourneyAnalyticItemSelectorDialogResult {
  numericMetadataFields: NumericMetadataField[];
}

@Component({
  selector: 'app-journey-analytic-item-selector-dialog',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [AppSharedModule, NumericMetadataFieldSelectorComponent],
  templateUrl: './journey-analytic-item-selector-dialog.component.html',
  styleUrl: './journey-analytic-item-selector-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JourneyAnalyticItemSelectorDialogComponent implements OnInit {
  options = input.required<JourneyAnalyticItemSelectorDialogOptions>();
  close = output<JourneyAnalyticItemSelectorDialogResult | null | ModalCloseType>();

  numericFields = signal<MetadataField[]>([]);
  isValid = signal(false);
  private numericMetadataFields: NumericMetadataField[] = [];

  private readonly destroyRef = inject(DestroyRef);
  private readonly accountService = inject(StorageAccountService);

  ngOnInit() {
    this.accountService.groupFieldsSub
      .takeUntilDestroyed(this.destroyRef)
      .subscribe((accountMetadata: AccountMetadata) => this.groupFieldSub(accountMetadata));
  }

  private groupFieldSub(accountMetadata: AccountMetadata): void {
    const dsMetadataFields = accountMetadata.metadata
      .filter((f) => this.options().metadataFields?.includes(f.fieldName))
      .map((f) => ({
        name: f.fieldName,
        label: f.description,
        type: f.fieldType,
        isRating: f.metadata?.rating === true,
        isRedacted: f.metadata?.redacted === true,
        isDisplayed: f.metadata?.display !== false,
        calculationFieldType: f.metadata?.calculationFieldType,
      }));
    if (this.options().widgetDataSource.hasDateData) {
      dsMetadataFields.push({
        type: 'date',
        name: 'createdAt',
        label: translate('analytic-items.createdAt'),
        isRating: false,
        isDisplayed: true,
        isRedacted: false,
        calculationFieldType: undefined,
      });
    }

    const filtered = dsMetadataFields.filter(
      (f) => f.isDisplayed && FieldService.hasNumerics(f.type, f.calculationFieldType)
    );
    filtered.push({
      name: 'sentimentScore',
      label: translate('analytic-items.sentiment'),
      type: 'double',
      isDisplayed: true,
      isRating: false,
      isRedacted: false,
      calculationFieldType: undefined,
    });
    this.numericFields.set(filtered);
  }

  onNumericMetadataFieldsChanged(fields: NumericMetadataField[]): void {
    this.numericMetadataFields = fields;
  }

  onNumericMetadataFieldsIsValid(isValid: boolean): void {
    this.isValid.set(isValid);
  }

  onSave(): void {
    const result = { numericMetadataFields: this.numericMetadataFields };
    this.close.emit(result);
  }

  onCancel(): void {
    this.close.emit(ModalCloseType.CANCEL);
  }
}
